import { motion } from 'motion/react';
import { useEffect } from 'react';

/**
 * Panel lateral para las acciones que no merecen ocupar sitio permanente.
 *
 * Crear un codigo o invitar a alguien son cosas que se hacen de vez en cuando;
 * dejarlas clavadas en la pantalla hace que lo primero que veas al entrar sea
 * trabajo por hacer en vez de lo que ya tienes. Aparece cuando lo pides y se va
 * cuando terminas.
 *
 * Se cierra con Escape y tocando fuera, que es lo que espera cualquiera.
 * Envolver en `AnimatePresence` para que la salida se vea.
 */
export function Cajon({
  titulo,
  onCerrar,
  children,
}: {
  readonly titulo: string;
  readonly onCerrar: () => void;
  readonly children: React.ReactNode;
}) {
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alPulsar);

    // Se bloquea el desplazamiento del fondo: si no, al llegar al final del
    // formulario la rueda empieza a mover la lista de detras.
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = previo;
    };
  }, [onCerrar]);

  return (
    <>
      <motion.div
        className="fixed inset-0 z-40 bg-black/45"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.16 }}
        onClick={onCerrar}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-line bg-surface"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 380, damping: 40 }}
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="m-0 text-base font-semibold">{titulo}</h2>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="grid size-8 place-items-center rounded-sm border border-line text-ink-2 hover:text-ink"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </motion.div>
    </>
  );
}
